"use client";

import { ArrowLeftIcon, ArrowRightIcon } from "@radix-ui/react-icons";
import classNames from "classnames";
import React, { useEffect, useState } from "react";
import { BlurImage } from "@/components/BlueImage";

export type ImageSliderProps = {
  images: Array<string>;
  interval?: number;
  theme?: string;
  className?: string;
};

const ImageSlider: React.FC<ImageSliderProps> = ({ images, interval = 2000, theme, className }) => {
  const [currentIndex, setCurrentIndex] = useState(1);

  // Automatically advance to the next image every 2 seconds
  useEffect(() => {
    const currentInterval = setInterval(() => {
      setCurrentIndex((current) => (current % images.length) + 1);
    }, interval);

    return () => clearInterval(currentInterval);
  }, [images.length, interval]);

  const previous = () => {
    setCurrentIndex(currentIndex > 1 ? currentIndex - 1 : images.length);
  };

  const forward = () => {
    setCurrentIndex(currentIndex < images.length ? currentIndex + 1 : 1);
  };

  return (
    <main
      className={classNames(
        "flex size-full place-content-center items-center justify-center bg-gray-700 p-7 sm:min-h-[500px]",
        className
      )}
      data-theme={theme}
    >
      {/*// mx-auto max-w-2xl overflow-hidden rounded-md bg-gray-100 p-2 sm:p-4*/}
      <div className="relative flex size-full items-center justify-center">
        <div className="absolute right-5 top-5 z-10 rounded-full bg-gray-600 px-2 text-center text-sm text-white">
          <span>{currentIndex}</span>/<span>{images.length}</span>
        </div>
        <button
          onClick={previous}
          className="absolute left-5 top-1/2 z-10 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-gray-100 shadow-md"
        >
          <ArrowLeftIcon className="text-2xl font-bold text-gray-500" />
        </button>
        <button
          onClick={forward}
          className="absolute right-5 top-1/2 z-10 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-gray-100 shadow-md"
        >
          <ArrowRightIcon className="text-2xl font-bold text-gray-500" />
        </button>
        <div className="relative flex size-full">
          {images.map((image, index) => (
            <div
              key={index}
              className={`flex size-full items-center justify-center transition-transform duration-300 ${
                currentIndex === index + 1 ? "" : "hidden"
              }`}
              style={{ transition: "opacity 0.3s" }}
            >
              <div className="flex size-full min-h-[350px] items-center justify-center">
                <BlurImage
                  src={image}
                  alt={`Slide ${index + 1}`}
                  className="rounded-sm"
                  imageClassName="object-fill"
                  lazy={false}
                  width={800}
                  height={800}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </main>
  );
};

export default ImageSlider;
